import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { 
  Leaf, 
  LayoutDashboard, 
  Plus, 
  User, 
  Settings,
  LogOut,
  Shield,
  ChevronLeft,
  ChevronRight,
  Target,
  Award,
  Flag,
  BarChart3,
  Bell,
  Newspaper,
  Info,
  Building2,
  CreditCard
} from "lucide-react";

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord" },
    { path: "/decision/new", icon: Plus, label: "Nouvelle décision" },
    { path: "/objectives", icon: Flag, label: "Objectifs RSE" },
    { path: "/organizations", icon: Building2, label: "Multi-Organisation" },
    { path: "/analytics", icon: BarChart3, label: "Analytics" },
    { path: "/alerts", icon: Bell, label: "Alertes" },
    { path: "/actualites", icon: Newspaper, label: "Actualités" },
    { path: "/pricing", icon: CreditCard, label: "Abonnement" },
    { path: "/profile", icon: Award, label: "Mon profil" },
  ];

  if (user?.role === "admin") {
    navItems.push({ path: "/admin", icon: Shield, label: "Administration" });
  }

  const isActive = (path) => location.pathname === path;

  return (
    <aside 
      className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''} bg-card border-r border-border flex flex-col transition-all duration-300`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className={`p-6 flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
          <Leaf className="w-6 h-6 text-primary-foreground" />
        </div>
        {!collapsed && (
          <div>
            <h1 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</h1>
            <p className="text-xs text-muted-foreground">CSRD/RSE Platform</p>
          </div>
        )}
      </div>

      <Separator />

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
              isActive(item.path)
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            } ${collapsed ? 'justify-center' : ''}`}
            data-testid={`nav-${item.path.replace('/', '')}`}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="font-medium">{item.label}</span>}
          </Link>
        ))}
      </nav>

      <Separator />

      {/* User Section */}
      <div className={`p-4 ${collapsed ? 'flex flex-col items-center gap-2' : ''}`}>
        {!collapsed && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-primary-foreground">
              {user?.full_name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.organization}</p>
            </div>
          </div>
        )}
        
        <Button
          variant="ghost"
          className={`w-full justify-start text-muted-foreground hover:text-destructive ${collapsed ? 'px-3' : ''}`}
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="ml-3">Déconnexion</span>}
        </Button>
      </div>

      {/* Collapse Toggle */}
      <div className="p-4 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={() => setCollapsed(!collapsed)}
          data-testid="toggle-sidebar-btn"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
